import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Modal } from 'react-bootstrap';
import AppContext from 'context/Context';
import UserDropdownV2 from 'components/dropdowns/CustomerServices/UserDropdownV2';
import { post, patch } from 'services/bs-kpi-board/kpiBoardService';
import {
  DEFAULT_WEEK_START_DAY,
  SIC_BOARD_TYPE,
  WEEK_START_DAY_OPTIONS
} from 'helpers/sicHelper';
import {
  swalToastError,
  swalToastSuccess
} from 'components/utilities/SwalToastUtil';

const buildInitialFormData = board => ({
  name: board.name ?? '',
  description: board.description ?? '',
  inChargeUserId: board.inChargeUserId ?? null,
  weekStartDay: board.weekStartDay ?? DEFAULT_WEEK_START_DAY
});

export default function SicBoardModal({ board, handleClose, handleSaved }) {
  const {
    config: { isDark }
  } = useContext(AppContext);
  const isEditing = Boolean(board.id);
  const [formData, setFormData] = useState(() => buildInitialFormData(board));
  const [isSaving, setIsSaving] = useState(false);

  const updateField = (field, value) =>
    setFormData(previous => ({ ...previous, [field]: value }));

  const handleSubmit = async event => {
    event.preventDefault();
    const request = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      inChargeUserId: formData.inChargeUserId,
      weekStartDay: Number(formData.weekStartDay)
    };

    setIsSaving(true);
    const response = isEditing
      ? await patch(board.id, request)
      : await post({ ...request, boardType: SIC_BOARD_TYPE.sic });
    setIsSaving(false);

    if (!response?.success) {
      swalToastError(response?.message || 'Failed to save board.');
      return;
    }
    swalToastSuccess(isEditing ? 'Board updated' : 'Board created');
    handleSaved();
  };

  return (
    <Modal show onHide={handleClose} contentClassName="border">
      <Form onSubmit={handleSubmit}>
        <Modal.Header
          closeButton
          closeVariant={isDark ? 'white' : undefined}
          className="bg-light px-card border-bottom-0"
        >
          <Modal.Title as="h5">
            {isEditing ? 'Edit' : 'Create'} SIC Board
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-card">
          <Form.Group className="mb-3">
            <Form.Label className="fs-0">Name</Form.Label>
            <Form.Control
              required
              value={formData.name}
              onChange={e => updateField('name', e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="fs-0">Description</Form.Label>
            <Form.Control
              value={formData.description}
              onChange={e => updateField('description', e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="fs-0">Person in Charge</Form.Label>
            <UserDropdownV2
              fieldName="inChargeUserId"
              handleChange={(field, user) => updateField(field, user?.userId)}
              placeholder="Person in Charge"
              value={formData.inChargeUserId}
              isMulti={false}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="fs-0">Week starts on</Form.Label>
            <Form.Select
              value={formData.weekStartDay}
              onChange={e => updateField('weekStartDay', e.target.value)}
            >
              {WEEK_START_DAY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="bg-light px-card border-top-0">
          <Button
            variant="primary"
            type="submit"
            className="px-4 mx-0"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

SicBoardModal.propTypes = {
  board: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSaved: PropTypes.func.isRequired
};
